/* eslint-disable unicorn/filename-case -- TODO rename later */
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useMergeRefs,
  useRole,
} from "@floating-ui/react";
import * as React from "react";
import { useContext, useLayoutEffect, useMemo, useState } from "react";

import { cn } from "~/utils";

import { Close } from "../../../assets/svg";
import Button from "../Button";
import type { ButtonVariant } from "../Button/Button";
import IconButton from "../IconButton";

interface DialogOptions {
  open: boolean;
  onHide: () => void;
}

function useDialog({ open, onHide }: DialogOptions) {
  const [labelId, setLabelId] = useState<string | undefined>();
  const [descriptionId, setDescriptionId] = useState<string | undefined>();
  const data = useFloating({
    open,
    onOpenChange(_nextOpen, _event, reason) {
      if (reason === "escape-key" || reason === "outside-press") {
        onHide();
      }
    },
  });

  const { context } = data;

  const click = useClick(context, {
    enabled: true,
  });
  const dismiss = useDismiss(context, {
    escapeKey: true,
    outsidePressEvent: "mousedown",
    outsidePress: (event) => {
      /**
       * Problem: The Create System modal was closing unexpectedly when interacting with the version Select dropdown. When Radix Select closes its portal, it dispatches an event that @floating-ui/react's useDismiss was interpreting as an "outside press" on the modal.
       * Root Cause: When the Select dropdown closes, the mousedown event has target: HTML with a minimal composedPath of only 3 elements (HTML → #document → Window). This is characteristic of events where the original target (the portal) has been unmounted from the DOM.
       * Fix: Added an outsidePress callback to useDismiss in the deprecated Modal component that ignores clicks where:
       * - The target is HTML (document root)
       * - The composedPath has ≤3 elements (indicating the original target was removed)
       */
      const target = event.target as HTMLElement;
      const isMinimalPath = event.composedPath().length <= 3;
      if (target.tagName === "HTML" && isMinimalPath) {
        return false;
      }
      return true;
    },
  });
  const role = useRole(context);

  const interactions = useInteractions([click, dismiss, role]);

  return useMemo(
    () => ({
      open,
      onHide,
      ...interactions,
      ...data,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
    }),
    [open, onHide, interactions, data, labelId, descriptionId],
  );
}

type ContextType =
  | (ReturnType<typeof useDialog> & {
      setLabelId: React.Dispatch<React.SetStateAction<string | undefined>>;
      setDescriptionId: React.Dispatch<React.SetStateAction<string | undefined>>;
    })
  | null;

const DialogContext = React.createContext<ContextType>(null);

const useDialogContext = () => {
  const context = useContext(DialogContext);

  if (context == null) {
    throw new Error("Dialog components must be wrapped in <Dialog />");
  }

  return context;
};

function Dialog({
  children,
  ...options
}: {
  children: React.ReactNode;
} & DialogOptions) {
  const dialog = useDialog(options);
  return <DialogContext.Provider value={dialog}>{children}</DialogContext.Provider>;
}

const DialogContainer = React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>((props, propRef) => {
  const { context: floatingContext, ...context } = useDialogContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  if (!floatingContext.open) return null;
  return (
    <FloatingPortal>
      <FloatingOverlay className="z-40 grid place-items-center bg-[#000000b3]" lockScroll>
        <FloatingFocusManager context={floatingContext} visuallyHiddenDismiss>
          <div
            ref={ref}
            aria-labelledby={context.labelId}
            aria-describedby={context.descriptionId}
            {...context.getFloatingProps(props)}
          >
            {props.children}
          </div>
        </FloatingFocusManager>
      </FloatingOverlay>
    </FloatingPortal>
  );
});

const DialogHeading = ({ title, onHide }: { title: string; onHide: () => void }) => {
  const { setLabelId } = useDialogContext();
  const id = useId();

  // Only sets `aria-labelledby` on the Dialog root element
  // if this component is mounted inside it.
  useLayoutEffect(() => {
    setLabelId(id);
    return () => setLabelId(undefined);
  }, [id, setLabelId]);

  return (
    <div className="flex items-center justify-between border-b border-gray-200 p-4">
      <span className="text-base font-semibold" id={id}>
        {title}
      </span>
      <IconButton
        dataTestId="close-modal-btn"
        icon={<Close />}
        label="Close modal"
        variant="transparent"
        onClick={onHide}
      />
    </div>
  );
};

const DialogContent = React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(
  ({ children, className, ...props }, ref) => {
    const { setDescriptionId } = useDialogContext();
    const id = useId();

    // Only sets `aria-describedby` on the Dialog root element
    // if this component is mounted inside it.
    useLayoutEffect(() => {
      setDescriptionId(id);
      return () => setDescriptionId(undefined);
    }, [id, setDescriptionId]);

    return (
      <div className={cn("p-4 text-sm", className)} {...props} ref={ref} id={id}>
        {children}
      </div>
    );
  },
);

type ModalActionButton = {
  dataTestId: string;
  disabled: boolean;
  label: string;
  loading: boolean;
  variant: ButtonVariant;
  clickHandler: () => void;
};

const DialogActions = ({
  actionButton,
  includeCancelButton,
  secondaryButton,
  onHide,
}: {
  actionButton?: ModalActionButton;
  includeCancelButton?: boolean;
  secondaryButton?: ModalActionButton;
  onHide: () => void;
}) => (
  <div className="flex items-center justify-center gap-x-2 border-t border-gray-200 p-4">
    {includeCancelButton && (
      <Button dataTestId="modal-cancel-button" label="Cancel" fullWidth variant="ternary" onClick={onHide} />
    )}
    {secondaryButton && (
      <Button
        dataTestId={secondaryButton.dataTestId}
        disabled={secondaryButton.disabled || secondaryButton.loading}
        fullWidth
        label={secondaryButton.label}
        variant={secondaryButton.variant}
        onClick={secondaryButton.clickHandler}
      />
    )}
    {actionButton && (
      <Button
        dataTestId={actionButton.dataTestId}
        disabled={actionButton.disabled || actionButton.loading}
        fullWidth
        label={actionButton.label}
        variant={actionButton.variant}
        onClick={actionButton.clickHandler}
      />
    )}
  </div>
);

/**
 * @deprecated Use `~/components/ui/dialog` instead.
 *
 * @example
 * import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "~/components/ui/dialog"
 */
const Modal = ({
  actionButton,
  content,
  includeCancelButton,
  open,
  secondaryButton,
  size = "sm",
  title,
  onHide,
}: {
  actionButton?: ModalActionButton;
  content: React.ReactNode;
  includeCancelButton: boolean;
  open: boolean;
  secondaryButton?: ModalActionButton;
  size?: "sm" | "lg";
  title: string;
  onHide: () => void;
}) => {
  const dialogSize = size === "sm" ? "w-[680px] bg-white" : "w-[1200px] bg-white";
  return (
    <Dialog open={open} onHide={onHide}>
      <DialogContainer className={dialogSize}>
        <DialogHeading onHide={onHide} title={title} />
        <DialogContent>{content}</DialogContent>
        <DialogActions
          includeCancelButton={includeCancelButton}
          onHide={onHide}
          actionButton={actionButton}
          secondaryButton={secondaryButton}
        />
      </DialogContainer>
    </Dialog>
  );
};

export default Modal;
